import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Settings, User, LogOut } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useLocalization } from '@/contexts/LocalizationContext'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'

export function UserMenu() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { t } = useLocalization()
  const { isBusinessClientMode } = useProfileSwitch()

  const handleSignOut = async () => {
    try {
      await logout()
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('Error during logout:', error)
      // Still navigate to login even if logout fails
      navigate('/login', { replace: true })
    }
  }

  // Generate initials from user's name
  const getInitials = () => {
    if (!user) return 'U'
    
    // Try first_name and last_name first
    if (user.first_name || user.last_name) {
      const first = user.first_name?.[0] || ''
      const last = user.last_name?.[0] || ''
      return (first + last).toUpperCase() || 'U'
    }
    
    // Fallback to name if available
    if (user.name) {
      return user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'U'
    }
    
    return 'U'
  }

  const initials = getInitials()
  
  // Get display name
  const getDisplayName = () => {
    if (!user) return t('common.user')
    
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || t('common.user')
    }
    
    return user.name || t('common.user')
  }

  const displayName = getDisplayName()
  
  // Get user identifier (ID, phone, or email)
  const getUserIdentifier = () => {
    if (!user) return ''
    // Prefer showing phone/email under the name (not internal ID).
    return user.phone_number || user.email || ''
  }

  const userIdentifier = getUserIdentifier()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 h-auto py-2 px-2 sm:px-3"
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="hidden sm:flex flex-col items-start">
            <span className="text-sm font-medium">{displayName}</span>
            {userIdentifier && (
              <span className="text-xs text-muted-foreground">{userIdentifier}</span>
            )}
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span>{displayName}</span>
            {userIdentifier && (
              <span className="text-xs font-normal text-muted-foreground">
                {user?.id ? `ID: ${user.id}` : user?.phone_number ? `Phone: ${user.phone_number}` : user?.email || ''}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/account-settings')}>
          <Settings className="me-2 h-4 w-4" />
          {t('nav.accountSettings')}
        </DropdownMenuItem>
        {!isBusinessClientMode && (
          <DropdownMenuItem onClick={() => navigate('/users')}>
            <User className="me-2 h-4 w-4" />
            {t('nav.userManagement')}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="me-2 h-4 w-4" />
          {t('nav.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
